import { FunctionComponent, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "../../Components/Select/AutoComplete";
import MultiSelect from "../../Components/MultiSelect/MultiSelect";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { createEditCourseTutorMappingAction, getAvailableTutors } from "../../Redux/Slices/CourseTutorMapping/CourseTutorMappingApi";
import { Grid, Box } from "@mui/material";
import { listCourseMaster } from "../../Redux/Slices/CourseMaster/ListCourseMasterSlice";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import { ICourseMaster } from "../../Pages/CourseMaster/ICourseMaster";

interface ICourseTutorMappingForm {
    course_tutor_map_id?: string;
    course_id: string;
    tutor_ids: AutocompleteOption[];
    status: boolean;
}

interface CourseTutorMappingFormProps {
    formRef: React.RefObject<HTMLFormElement>;
    initialValues: ICourseTutorMappingForm | {};
    setInitialValues: React.Dispatch<React.SetStateAction<ICourseTutorMappingForm | {}>>;
}

const schema = yup.object().shape({
    course_id: yup.string().required("Course is required"),
    tutor_ids: yup.array().of(yup.object().shape({ label: yup.string(), value: yup.mixed() })).required("At least one tutor is required").min(1, "At least one tutor is required"),
    status: yup.boolean().required("Status is required"),
});


const CourseTutorMappingForm: FunctionComponent<CourseTutorMappingFormProps> = ({
    formRef,
    initialValues,
    setInitialValues,
}) => {
    const dispatch = useAppDispatch();
    const { data: courseList } = useAppSelector((state) => state.ListCourseMasterSlice);
    const availableTutorData = useAppSelector((state) => state.CourseTutorMapping?.availableTutorData);

    const methods = useForm<ICourseTutorMappingForm>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            course_tutor_map_id: "",
            course_id: "",
            tutor_ids: [],
            status: true,
        },
        mode: "onChange",
    });

    const { handleSubmit, reset, watch, setValue } = methods;

    useEffect(() => {
        dispatch(listCourseMaster());
    }, [dispatch]);

    useEffect(() => {
        if (initialValues && Object.keys(initialValues).length > 0) {
            reset(initialValues as ICourseTutorMappingForm);
        }
    }, [initialValues, reset]);

    const onSubmit = (data: ICourseTutorMappingForm) => {
        const submitData = {
            ...data,
            course_tutor_map_id: data.course_tutor_map_id || "0",
            tutor_ids: data.tutor_ids?.map((tutor: AutocompleteOption) => tutor.value as number) || [],
            is_delete: false,
        };
        dispatch(createEditCourseTutorMappingAction(submitData));
    };

    // Filter courses for dropdown
    const courseOptions = Array.isArray(courseList?.data)
        ? courseList?.data.map((course: ICourseMaster) => ({
            value: course.course_id || course.id?.toString() || '',
            label: course.full_name || course.course_code || 'Unknown Course',
        }))
        : [];

    // Available tutors from the dedicated endpoint
    const tutorOptions = Array.isArray(availableTutorData?.data)
        ? availableTutorData?.data.map((tutor: any) => ({
            value: tutor.id ?? tutor.tutor_id,
            label: tutor.student_name || tutor.full_name || tutor.name || tutor.username || 'Unknown',
        }))
        : [];

    const statusOptions = [
        { value: true, label: "Active" },
        { value: false, label: "Inactive" },
    ];

    const course_id = watch("course_id");
    const tutor_ids = watch("tutor_ids");
    const status = watch("status");

    return (
        <FormProvider {...methods}>
            <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ pt: 2, px: 1 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={12}>
                            <Select
                                name="course_id"
                                label="Course"
                                options={courseOptions}
                                value={courseOptions?.find((opt: AutocompleteOption) => opt.value === course_id) ?? null}
                                onChange={(_, value) => {
                                    const val = value as AutocompleteOption;
                                    setValue("course_id", val?.value as string, { shouldValidate: true, shouldDirty: true });
                                }}
                                required
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <MultiSelect
                                name="tutor_ids"
                                label="Tutors"
                                options={tutorOptions}
                                value={(tutor_ids as any[]) || []}
                                onChange={(_, value) => {
                                    const values = value as AutocompleteOption[];
                                    setValue("tutor_ids", values || [], { shouldValidate: true, shouldDirty: true });
                                }}
                                fullWidth
                                getOptionLabel={(option: AutocompleteOption | string) => typeof option === 'string' ? option : option?.label || ''}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <Select
                                name="status"
                                label="Status"
                                options={statusOptions}
                                value={statusOptions?.find((opt: AutocompleteOption) => opt.value === status) ?? null}
                                onChange={(_, value) => {
                                    const val = value as AutocompleteOption;
                                    setValue("status", val?.value as boolean, { shouldValidate: true, shouldDirty: true });
                                }}
                                required
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Box>
            </form>
        </FormProvider>
    );
};

export default CourseTutorMappingForm;
